/**
 * First Time Setup Page
 * Staff must change their password and PIN on first login
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Link2, 
  Lock, 
  KeyRound, 
  Eye, 
  EyeOff, 
  Loader2,
  Shield,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const FirstTimeSetup = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
    new_pin: '',
    confirm_pin: ''
  });
  
  const [errors, setErrors] = useState({});
  const [userName, setUserName] = useState('');

  useEffect(() => {
    checkFirstLoginStatus();
  }, []);

  const checkFirstLoginStatus = async () => {
    try {
      const response = await api.get('/first-login/status');
      if (!response.data.must_change_password) {
        // User doesn't need to change password, redirect to dashboard
        navigate('/admin', { replace: true });
        return;
      }
      setUserName(response.data.user_name || 'Staff Member');
    } catch (error) {
      console.error('Error checking first login status:', error);
      // If error, assume they need to change password
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Password validation
    if (!formData.new_password) {
      newErrors.new_password = 'Password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    // PIN validation
    if (!formData.new_pin) {
      newErrors.new_pin = 'PIN is required';
    } else if (!/^\d{4,6}$/.test(formData.new_pin)) {
      newErrors.new_pin = 'PIN must be 4-6 digits';
    }
    
    if (!formData.confirm_pin) {
      newErrors.confirm_pin = 'Please confirm your PIN';
    } else if (formData.new_pin !== formData.confirm_pin) {
      newErrors.confirm_pin = 'PINs do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/first-login/change-credentials', formData);
      toast.success('Credentials updated successfully!');
      
      // Update localStorage directly to prevent redirect loop
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.must_change_password = false;
      localStorage.setItem('user', JSON.stringify(storedUser));
      
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
      
      // Small delay to ensure state propagates, then redirect based on role
      setTimeout(() => {
        const roles = storedUser.roles || [];
        if (roles.includes('ADMIN') || roles.includes('CEO') || roles.includes('STAFF')) {
          window.location.href = '/admin';
        } else if (roles.includes('DRIVER')) {
          window.location.href = '/driver';
        } else if (roles.includes('BUSINESS_MERCHANT')) {
          window.location.href = '/merchant';
        } else if (roles.includes('PUDO_POINT') || roles.includes('PUDO_OPERATOR')) {
          window.location.href = '/pudo';
        } else {
          window.location.href = '/dashboard';
        }
      }, 200);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update credentials';
      toast.error(message);
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Link2 className="h-10 w-10 text-amber-500" />
            <span className="text-3xl font-bold text-white tracking-tight">Liantiao</span>
          </div>
          <p className="text-slate-400">The Chain - Jamaica's Logistics Network</p>
        </div>

        {/* Setup Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome, {userName}!</h1>
            <p className="text-slate-400 text-sm">
              For security, please set up your new password and PIN before accessing your dashboard.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-slate-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password (min 8 characters)"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  className={`bg-slate-950 border-slate-800 text-white pr-10 ${errors.new_password ? 'border-red-500' : 'focus:border-amber-500'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.new_password && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.new_password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-slate-300">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  data-testid="confirm-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className={`bg-slate-950 border-slate-800 text-white pr-10 ${errors.confirm_password ? 'border-red-500' : 'focus:border-amber-500'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm_password && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.confirm_password}
                </p>
              )}
              {formData.new_password && formData.confirm_password && formData.new_password === formData.confirm_password && (
                <p className="text-green-400 text-xs flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Passwords match
                </p>
              )}
            </div>

            <hr className="border-slate-800 my-6" />

            {/* New PIN */}
            <div className="space-y-2">
              <Label htmlFor="new_pin" className="text-slate-300 flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                New PIN (4-6 digits)
              </Label>
              <div className="relative">
                <Input
                  id="new_pin"
                  data-testid="new-pin-input"
                  type={showPin ? 'text' : 'password'}
                  placeholder="Enter 4-6 digit PIN"
                  value={formData.new_pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFormData({ ...formData, new_pin: value });
                  }}
                  maxLength={6}
                  className={`bg-slate-950 border-slate-800 text-white pr-10 ${errors.new_pin ? 'border-red-500' : 'focus:border-amber-500'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.new_pin && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.new_pin}
                </p>
              )}
            </div>

            {/* Confirm PIN */}
            <div className="space-y-2">
              <Label htmlFor="confirm_pin" className="text-slate-300">
                Confirm PIN
              </Label>
              <div className="relative">
                <Input
                  id="confirm_pin"
                  data-testid="confirm-pin-input"
                  type={showConfirmPin ? 'text' : 'password'}
                  placeholder="Re-enter your PIN"
                  value={formData.confirm_pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFormData({ ...formData, confirm_pin: value });
                  }}
                  maxLength={6}
                  className={`bg-slate-950 border-slate-800 text-white pr-10 ${errors.confirm_pin ? 'border-red-500' : 'focus:border-amber-500'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPin(!showConfirmPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showConfirmPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm_pin && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.confirm_pin}
                </p>
              )}
              {formData.new_pin && formData.confirm_pin && formData.new_pin === formData.confirm_pin && (
                <p className="text-green-400 text-xs flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> PINs match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="setup-submit-btn"
              disabled={submitting}
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 h-11 font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Complete Setup
                </>
              )}
            </Button>
          </form>

          {/* Logout option */}
          <div className="mt-6 text-center">
            <button
              onClick={handleLogout}
              className="text-slate-400 text-sm hover:text-white transition-colors"
            >
              Sign out and return later
            </button>
          </div>
        </div>

        {/* Security Note */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Your credentials are encrypted and securely stored. Never share your password or PIN with anyone.
        </p>
      </div>
    </div>
  );
};

export default FirstTimeSetup;
